import { useNavigate } from '@tanstack/react-router';
import { GraduationCap, Loader2, RefreshCcw } from 'lucide-react';
import { useState } from 'react';

import { useGetFamilyByStudent } from '~entities/family';
import { useGetOutstandingBalance } from '~entities/student/api/get-outstanding-balance';
import { useGetStudentById } from '~entities/student/api/get-student-by-id';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { AlterMonthlyTuition } from '~features/students/ui/student-payment/alter-monthly-tuition';
import { EnrollStudentModal } from '~features/students/ui/student-payment/enroll-students';
import { ExportStudentPaymentHistory } from '~features/students/ui/student-payment/export-student-payment-history';
import { ManuallyCharge } from '~features/students/ui/student-payment/manually-charge';
import { RecordCashCheckPayment } from '~features/students/ui/student-payment/record-cash-check-payment';
import { DIRECT_INVOICE_PAYMENT_UI_ENABLED } from '~features/students/ui/student-payment/direct-invoice-payment-enabled';
import { TakePaymentDialog } from '~features/students/ui/student-payment/take-payment-dialog';
import { StopChargingStudent } from '~features/students/ui/student-payment/stop-charging-student';
import { StudentPaymentHistoryTable } from '~features/students/ui/student-payment/student-payment-history-table';
import { ViewUpcomingCharges } from '~features/students/ui/student-payment/view-upcoming-charges';
import { WalletBalanceCard } from '~features/students/ui/student-payment/wallet-balance-card';

import { useGetCurrency } from '~shared/api/currency-api';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';

export interface UnpaidInvoice {
  id: string;
  date: string;
  product: string;
  type: string;
  amount: number;
  paid: number;
  outstanding: number;
  paused: boolean;
}

export interface StudentPaymentHistoryPageProps {
  studentId: string;
}

export const StudentPaymentHistoryPage = ({
  studentId,
}: StudentPaymentHistoryPageProps) => {
  const navigate = useNavigate();
  const { data: currencyDetails, isFetching: isCurrencyLoading } =
    useGetCurrency();
  const {
    data,
    isFetching: isOutstandingBalanceLoading,
    refetch: refetchOutstandingBalance,
  } = useGetOutstandingBalance(studentId);
  const { hasAdminAccess } = useCurrentUserRole();

  const { data: student, isFetching: isStudentLoading } =
    useGetStudentById(studentId);
  const { data: familyByStudent } = useGetFamilyByStudent(studentId);
  // V2: wallet / charge actions follow current billing parent, not legacy owner
  // (ENR-4054 smoke — credit landed on father after mother was set billing).
  const billingParentId =
    familyByStudent?.family?.billingParentId || student?.parentId || '';

  const [enrollStudentDialogOpen, setEnrollStudentDialogOpen] = useState(false);

  if (isStudentLoading) {
    return (
      <div className='flex h-48 items-center justify-center'>
        <Loader2 className='size-10 animate-spin' />
      </div>
    );
  }

  return (
    <div className='flex min-h-screen w-full flex-col'>
      <div className='flex flex-1 flex-col gap-6 md:gap-8'>
        {/* Page Header */}
        <div className='flex items-center justify-between'>
          <h1 className='text-2xl font-semibold'>Student Payments</h1>
        </div>

        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <WalletBalanceCard
            parentId={billingParentId}
            studentId={studentId}
          />

          <Card className='flex flex-col'>
            <CardHeader className='p-4 pb-2'>
              <CardTitle className='flex items-center gap-2 text-sm font-medium text-muted-foreground'>
                Total Outstanding Balance
                <Button
                  variant='ghost'
                  size='sm'
                  className='size-8 p-0'
                  onClick={() => refetchOutstandingBalance()}
                >
                  <RefreshCcw className='size-4' />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className='flex flex-1 flex-col justify-center p-4 py-2'>
              {isOutstandingBalanceLoading || isCurrencyLoading ? (
                <div className='flex h-9 items-center justify-start'>
                  <Loader2 className='size-6 animate-spin' />
                </div>
              ) : (
                <div
                  className={`text-3xl font-bold ${data?.totalOutstanding ? 'text-red-600' : 'text-green-600'}`}
                >
                  {currencyDetails?.symbol ?? '$'}
                  {data?.totalOutstanding ?? '0.00'}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className='p-4 pb-2'>
            <CardTitle className='text-sm font-medium text-muted-foreground'>
              Account Actions
            </CardTitle>
          </CardHeader>
          <CardContent className='grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4'>
            {hasAdminAccess && (
              <StopChargingStudent
                studentId={studentId}
                studentName={student?.name ?? ''}
              />
            )}
            {hasAdminAccess && (
              <AlterMonthlyTuition studentId={studentId} />
            )}
            <ViewUpcomingCharges studentId={studentId} />
            {hasAdminAccess && (
              <ManuallyCharge
                studentId={studentId}
                parentId={billingParentId}
              />
            )}
          </CardContent>
        </Card>

        <Card className='w-full'>
          <CardHeader className='pb-2'>
            <div className='flex flex-col space-y-2'>
              <div className='flex items-center justify-between'>
                <div>
                  <CardTitle>Transaction History</CardTitle>
                  <CardDescription>
                    View all payment transactions for this student.
                  </CardDescription>
                </div>
              </div>

              {/* Table Toolbar */}
              <div className='flex flex-nowrap items-center gap-2 pt-2 md:flex-wrap md:gap-4'>
                {DIRECT_INVOICE_PAYMENT_UI_ENABLED &&
                  hasAdminAccess &&
                  billingParentId && (
                    <TakePaymentDialog
                      parentId={billingParentId}
                      studentId={studentId}
                    />
                  )}
                {hasAdminAccess && (
                  <RecordCashCheckPayment
                    studentId={studentId}
                    parentId={billingParentId}
                  />
                )}
                <Button
                  variant='secondary'
                  size='sm'
                  className='shrink-0'
                  title='Enroll Student'
                  aria-label='Enroll Student'
                  onClick={() => {
                    setEnrollStudentDialogOpen(true);
                  }}
                >
                  <GraduationCap />
                  <span className='sr-only md:not-sr-only md:whitespace-nowrap'>
                    Enroll Student
                  </span>
                </Button>

                <EnrollStudentModal
                  studentId={studentId}
                  dialogOpen={enrollStudentDialogOpen}
                  setDialogOpen={setEnrollStudentDialogOpen}
                  onTrialEnrollmentSuccess={() => {
                    void navigate({
                      to: '/students/$studentId/classes',
                      params: { studentId },
                    });
                  }}
                />

                <div className='hidden grow md:block'></div>

                <ExportStudentPaymentHistory studentId={studentId} />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <StudentPaymentHistoryTable studentId={studentId} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
